import React from 'react';
import Button from '@material-ui/core/Button';
import { makeStyles, type Theme } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import ArrowIcon from '@material-ui/icons/ArrowDropDown';
import { colors } from '@skyslope/mache';
import { useGetUserAndAccountSettings } from '@/lib/api/_hooks';
import type { ITagFilters } from '../../containers/TemplateManagement';
import { tagIcons } from '../../lib/constants';
import { Pill } from '../common';
import { StyledMenu, StyledMenuItem } from '../common/StyledMenu';

const useStyles = makeStyles((theme: Theme) => ({
  wrapper: {
    padding: theme.spacing(4),
    borderBottom: `1px solid ${colors.grey[400]}`,
  },
  filterText: {
    marginRight: theme.spacing(1),
  },
  buttons: {
    display: 'flex',
    alignItems: 'center',
  },
  button: {
    display: 'flex',
    alignItems: 'center',
    color: colors.grey[800],
  },
  buttonIcon: {
    color: colors.grey[500],
    marginRight: theme.spacing(1),
  },
  pills: {
    display: 'flex',
  },
  menu: {
    minWidth: 120,
    maxHeight: 360,
  },
}));

interface IProps {
  tagFilters: ITagFilters;
  setTagFilters: (tagFilters: ITagFilters) => void;
}

export function TaggingFilters(props: IProps) {
  const { tagFilters, setTagFilters } = props;

  const settings = useGetUserAndAccountSettings();
  const tagTypes = settings.data?.account.template?.tagging?.tagTypes || [];

  const classes = useStyles();
  const [openTagId, setOpenTagId] = React.useState('');
  const buttonRef = React.useRef(null);

  function openMenu(event: any, tagId: string) {
    setOpenTagId(tagId);
    buttonRef.current = event.currentTarget;
  }

  function closeMenu() {
    setOpenTagId('');
    buttonRef.current = null;
  }

  function changeFilter(tagId: string, tagValue?: string) {
    const newFilters = { ...tagFilters };
    if (tagValue) {
      newFilters[tagId] = tagValue;
      closeMenu();
    } else {
      delete newFilters[tagId];
    }
    setTagFilters(newFilters);
  }

  return (
    <div className={classes.wrapper}>
      <div className={classes.buttons}>
        <Typography variant="body1" className={classes.filterText}>
          Filter results by:
        </Typography>
        {tagTypes.map((tt) => (
          <Button key={tt.id} onClick={(e) => openMenu(e, tt.id)}>
            <div className={classes.button} data-spec="tag-filter">
              {/* @ts-expect-error -- This code was written prior to enforcing lint rules */}
              {tt.iconName && <div className={classes.buttonIcon}>{tagIcons[tt.iconName]}</div>}
              {tt.name}
              <ArrowIcon />
            </div>
          </Button>
        ))}
      </div>
      <StyledMenu
        anchorEl={buttonRef.current}
        keepMounted
        open={Boolean(openTagId)}
        onClose={closeMenu}
        classes={{ paper: classes.menu }}
        autoFocus={false}
      >
        {openTagId
          ? tagTypes
              .find((tt) => tt.id === openTagId)!
              .options.map((o) => {
                return (
                  <StyledMenuItem key={o} onClick={() => changeFilter(openTagId, o)}>
                    {o}
                  </StyledMenuItem>
                );
              })
          : null}
      </StyledMenu>
      <div className={classes.pills}>
        {Object.keys(tagFilters).map((tagId: string) => (
          <Pill
            key={tagId}
            id={tagId}
            text={tagFilters[tagId]}
            onDelete={() => changeFilter(tagId)}
            // @ts-expect-error -- This code was written prior to enforcing lint rules
            icon={tagIcons[tagTypes.find((tt) => tt.id === tagId)!.iconName || '']}
          />
        ))}
      </div>
    </div>
  );
}
